import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import getApiServices from "../../services/getApiService";
import { getOrPostSites } from "../../constants/api-end-pionts";
import SiteSidebar from "./components/SiteSidebar";
import SystemList from "./components/SystemList";
import SensorList from "./components/SensorList";
import SetCriticalValueModal from "./components/SetCriticalValueModal";
import { FaSitemap, FaBolt, FaChevronRight, FaMicrochip } from "react-icons/fa";
import { CiSettings } from "react-icons/ci";
import { formatDisplayName } from "../../utils/textUtils";

type MobileTab = "sites" | "systems" | "sensors" | "all";

const SiteList: React.FC = () => {
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null);

  // Responsive mobile/tablet navigation tab
  const [activeMobileTab, setActiveMobileTab] = useState<MobileTab>("sites");

  // State for Critical Value Configuration Modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [siteToEdit, setSiteToEdit] = useState<any | null>(null);

  const { data: sitesData, isPending } = useQuery({
    queryKey: ["sites", page],
    queryFn: async () => getApiServices(getOrPostSites + `?page=${page}&size=${5}`),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const [localSites, setLocalSites] = useState<any[]>([]);

  useEffect(() => {
    setTotalPages(sitesData?.totalPages || 1);
    const content = sitesData?.content || [];
    setLocalSites(content);
    if (content.length && !selectedSiteId) {
      const firstSite = content[0];
      setSelectedSiteId(firstSite.id);
      setSelectedSystemId(firstSite.systems?.[0]?.id || null);
    }
  }, [sitesData, selectedSiteId]);

  const selectedSite = useMemo(
    () => localSites.find((site: any) => site.id === selectedSiteId) || null,
    [localSites, selectedSiteId]
  );

  const systems = useMemo(() => selectedSite?.systems || [], [selectedSite]);

  const selectedSystem = useMemo(
    () => systems.find((sys: any) => sys.id === selectedSystemId) || null,
    [systems, selectedSystemId]
  );

  const totalSensorsInSystem = selectedSystem?.sensors?.length || 0;

  const handleOpenEditModal = useCallback((site: any) => {
    setSiteToEdit(site);
    setIsModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsModalOpen(false);
    setSiteToEdit(null);
  }, []);

  // When a site is selected on mobile/tablet, auto-advance to systems view
  const handleSiteSelect = useCallback((_site: any) => {
    // Only switch tab if we are in mobile/tablet mode and not on "all"
    if (typeof window !== "undefined" && window.innerWidth < 1280) {
      setActiveMobileTab("systems");
    }
  }, []);

  // When a system is selected on mobile/tablet, auto-advance to sensors view
  const handleSystemSelect = useCallback((_system: any) => {
    if (typeof window !== "undefined" && window.innerWidth < 1280) {
      setActiveMobileTab("sensors");
    }
  }, []);

  const thresholdValue =
    selectedSite?.criticalResistanceValue != null
      ? Number(selectedSite.criticalResistanceValue).toFixed(1)
      : "3.0";

  return (
    <div
      className="flex flex-col min-h-[calc(100vh-64px)] p-3 sm:p-4 md:p-6 text-white"
      style={{
        background: "linear-gradient(160deg, #070c18 0%, #0a0f1e 60%, #08101f 100%)",
      }}
    >
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3.5 mb-4 sm:mb-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div
            className="p-2.5 rounded-2xl flex-shrink-0 shadow-lg"
            style={{
              background:
                "linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(99,102,241,0.15) 100%)",
              border: "1px solid rgba(59,130,246,0.3)",
              boxShadow: "0 4px 20px rgba(59,130,246,0.2)",
            }}
          >
            <FaSitemap className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              Sites
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-0.5">
              Manage facility sites, resistance limits, and grounding systems
            </p>
          </div>
        </div>

        {/* Top Header Controls & Stats */}
        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
          {/* Sites & Systems Quick Stats */}
          <div className="hidden md:flex items-center gap-2">
            <span
              className="text-xs px-3 py-1.5 rounded-xl border font-semibold text-slate-300"
              style={{
                background: "rgba(255,255,255,0.03)",
                borderColor: "rgba(255,255,255,0.08)",
              }}
            >
              {localSites.length} {localSites.length === 1 ? "Site" : "Sites"}
            </span>
            {selectedSite && (
              <span
                className="text-xs px-3 py-1.5 rounded-xl border font-semibold text-purple-300"
                style={{
                  background: "rgba(168,85,247,0.1)",
                  borderColor: "rgba(168,85,247,0.25)",
                }}
              >
                {systems.length} Systems
              </span>
            )}
          </div>

          {/* Critical Threshold Action Button */}
          {selectedSite && (
            <button
              type="button"
              onClick={() => handleOpenEditModal(selectedSite)}
              className="px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-xl text-xs font-bold text-white flex items-center gap-2 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 transition-all cursor-pointer shadow-sm hover:shadow-[0_0_15px_rgba(245,158,11,0.25)] active:scale-95"
              title="Configure critical resistance threshold for selected site"
            >
              <FaBolt className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
              <span>Limit: {thresholdValue} Ω</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Interactive Breadcrumbs Bar ──────────────────────────── */}
      <div
        className="flex items-center gap-1.5 sm:gap-2 py-2 px-3 sm:px-4 rounded-xl mb-3 sm:mb-4 text-xs overflow-x-auto custom-scrollbar flex-shrink-0"
        style={{
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <button
          onClick={() => setActiveMobileTab("sites")}
          className="text-slate-400 hover:text-white transition-colors font-medium flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
        >
          <FaSitemap className="w-3 h-3 text-blue-400" />
          <span>Sites ({localSites.length})</span>
        </button>

        {selectedSite && (
          <>
            <FaChevronRight className="w-2.5 h-2.5 text-slate-600 flex-shrink-0" />
            <button
              onClick={() => setActiveMobileTab("systems")}
              className="text-blue-300 hover:text-white transition-colors font-semibold truncate max-w-[140px] sm:max-w-[220px] cursor-pointer whitespace-nowrap"
            >
              {formatDisplayName(selectedSite.name)}
            </button>
          </>
        )}

        {selectedSystem && (
          <>
            <FaChevronRight className="w-2.5 h-2.5 text-slate-600 flex-shrink-0" />
            <button
              onClick={() => setActiveMobileTab("sensors")}
              className="text-purple-300 hover:text-white transition-colors font-semibold truncate max-w-[140px] sm:max-w-[220px] cursor-pointer whitespace-nowrap"
            >
              {formatDisplayName(selectedSystem.name)}
            </button>
            <span className="text-slate-500 text-[11px] whitespace-nowrap hidden xs:inline">
              ({totalSensorsInSystem} {totalSensorsInSystem === 1 ? "pit" : "pits"})
            </span>
          </>
        )}
      </div>

      {/* ── Mobile & Tablet Segmented Tab Bar (< xl) ─────────────── */}
      <div
        className="xl:hidden flex items-center p-1 rounded-2xl mb-3 sm:mb-4 gap-1 overflow-x-auto custom-scrollbar flex-shrink-0"
        style={{
          background: "rgba(13,21,38,0.75)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <button
          onClick={() => setActiveMobileTab("sites")}
          className={`flex-1 min-w-[90px] py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "sites"
              ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <FaSitemap className="w-3 h-3" />
          <span>Sites</span>
          <span className="text-[10px] opacity-75 font-mono">({localSites.length})</span>
        </button>

        <button
          onClick={() => setActiveMobileTab("systems")}
          className={`flex-1 min-w-[95px] py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "systems"
              ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <CiSettings className="w-3.5 h-3.5 stroke-1" />
          <span>Systems</span>
          <span className="text-[10px] opacity-75 font-mono">({systems.length})</span>
        </button>

        <button
          onClick={() => setActiveMobileTab("sensors")}
          className={`flex-1 min-w-[100px] py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "sensors"
              ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <FaMicrochip className="w-3 h-3" />
          <span>Earth Pits</span>
          <span className="text-[10px] opacity-75 font-mono">({totalSensorsInSystem})</span>
        </button>

        <button
          onClick={() => setActiveMobileTab("all")}
          className={`py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap ${
            activeMobileTab === "all"
              ? "bg-slate-700 text-white shadow-sm"
              : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
          }`}
        >
          <span>All Panels</span>
        </button>
      </div>

      {/* ── 3-panel Responsive Layout ────────────────────────────── */}
      <div className="flex flex-col xl:flex-row gap-4 sm:gap-5 flex-1 min-h-0 xl:h-[calc(100vh-190px)]">
        {/* Panel 1: Site Sidebar */}
        <div
          className={`${
            activeMobileTab === "sites" || activeMobileTab === "all"
              ? "block"
              : "hidden xl:block"
          } xl:w-80 2xl:w-[340px] flex-shrink-0 flex flex-col`}
        >
          <SiteSidebar
            sites={localSites}
            isPending={isPending}
            selectedSiteId={selectedSiteId}
            setSelectedSiteId={setSelectedSiteId}
            setSelectedSystemId={setSelectedSystemId}
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            onEditCriticalValue={handleOpenEditModal}
            onSelectSiteCallback={handleSiteSelect}
          />
        </div>

        {/* Panel 2: Systems List */}
        <div
          className={`${
            activeMobileTab === "systems" || activeMobileTab === "all"
              ? "block"
              : "hidden xl:block"
          } flex-1 min-w-0 flex flex-col`}
        >
          <SystemList
            selectedSite={selectedSite}
            systems={systems}
            selectedSystemId={selectedSystemId}
            setSelectedSystemId={setSelectedSystemId}
            onEditCriticalValue={handleOpenEditModal}
            onSelectSystemCallback={handleSystemSelect}
          />
        </div>

        {/* Panel 3: Connected Earth Pits */}
        <div
          className={`${
            activeMobileTab === "sensors" || activeMobileTab === "all"
              ? "block"
              : "hidden xl:block"
          } xl:w-84 2xl:w-96 flex-shrink-0 flex flex-col`}
        >
          <SensorList
            selectedSystem={selectedSystem}
            criticalThreshold={selectedSite?.criticalResistanceValue}
          />
        </div>
      </div>

      {/* ── Set Critical Value Modal ─────────────────────────────── */}
      <SetCriticalValueModal
        isOpen={isModalOpen}
        onClose={handleCloseEditModal}
        site={siteToEdit}
      />
    </div>
  );
};

export default React.memo(SiteList);
